import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../services/db';

type TestState = 'intro' | 'test' | 'results';

const questions = [
  {
    id: 1,
    type: 'boolean',
    question: "Der EU AI Act verpflichtet Unternehmen, die Kompetenz ihrer Mitarbeitenden beim Umgang mit KI-Systemen nachzuweisen.",
    options: ["Ja", "Nein"],
    correctAnswer: 0
  },
  {
    id: 2,
    type: 'multiple',
    question: "Welche Begriffe zählen zur Familie der Künstlichen Intelligenz?",
    options: [
      "Maschinelles Lernen",
      "Blockchain",
      "Deep Learning",
      "Natural Language Processing (NLP)"
    ],
    correctAnswers: [0, 2, 3]
  },
  {
    id: 3,
    type: 'boolean',
    question: "Es ist erlaubt, vertrauliche Unternehmensdaten in öffentliche KI-Systeme wie ChatGPT einzutragen.",
    options: ["Ja", "Nein"],
    correctAnswer: 1
  },
  {
    id: 4,
    type: 'multiple',
    question: "Was sind typische Risiken beim Einsatz von KI?",
    options: [
      "Transparenzverlust",
      "Fehlerfreiheit",
      "Datenverzerrung (Bias)",
      "Datenschutzverletzungen"
    ],
    correctAnswers: [0, 2, 3]
  },
  {
    id: 5,
    type: 'boolean',
    question: "Jede KI-Entscheidung ist immer objektiv und neutral.",
    options: ["Ja", "Nein"],
    correctAnswer: 1
  },
  {
    id: 6,
    type: 'single',
    question: "Was ist KEINE typische KI-Anwendung?",
    options: [
      "Erstellung von Texten",
      "Bildanalyse",
      "Vertragsunterschrift mit Kugelschreiber",
      "Kundensupport-Automatisierung"
    ],
    correctAnswer: 2
  },
  {
    id: 7,
    type: 'boolean',
    question: "Es ist sinnvoll, Quellenangaben bei KI-generierten Inhalten zu überprüfen.",
    options: ["Ja", "Nein"],
    correctAnswer: 0
  },
  {
    id: 8,
    type: 'multiple',
    question: "Welche Grundregeln gelten für sicheren KI-Einsatz?",
    options: [
      "Niemals sensible Daten eingeben",
      "Blindes Vertrauen in Ergebnisse",
      "Kritisch prüfen",
      "Prompts klar formulieren"
    ],
    correctAnswers: [0, 2, 3]
  },
  {
    id: 9,
    type: 'boolean',
    question: "Die Nutzung von KI ist risikofrei, solange man sie richtig bedient.",
    options: ["Ja", "Nein"],
    correctAnswer: 1
  },
  {
    id: 10,
    type: 'single',
    question: "Was beschreibt der Begriff 'Bias' in der KI?",
    options: [
      "Technisches Versagen",
      "Rechenfehler",
      "Verzerrte Ergebnisse durch einseitige Daten",
      "Hohe Geschwindigkeit"
    ],
    correctAnswer: 2
  },
  {
    id: 11,
    type: 'boolean',
    question: "Eine interne Richtlinie zur KI-Nutzung erhöht die Sicherheit im Unternehmen.",
    options: ["Ja", "Nein"],
    correctAnswer: 0
  },
  {
    id: 12,
    type: 'multiple',
    question: "Welche Ziele verfolgt der EU AI Act?",
    options: [
      "Sicherstellung von Transparenz",
      "Schutz der Grundrechte", 
      "Verpflichtung zur flächendeckenden KI-Nutzung",
      "Abschaffung menschlicher Aufsicht"
    ],
    correctAnswers: [0, 1]
  },
  {
    id: 13,
    type: 'boolean',
    question: "KI-Systeme benötigen qualitativ hochwertige Daten, um gute Ergebnisse zu liefern.",
    options: ["Ja", "Nein"],
    correctAnswer: 0
  },
  {
    id: 14,
    type: 'single',
    question: "Welche Rolle spielt ein Kompetenznachweis im Kontext des EU AI Act?",
    options: [
      "Er ersetzt alle anderen Schulungen",
      "Er dient als Nachweis, dass Nutzer Verantwortung und Risiken verstehen",
      "Er erlaubt jegliche Nutzung ohne Kontrolle",
      "Er ist nur bei Hochrisiko-KI nötig"
    ],
    correctAnswer: 1
  },
  {
    id: 15,
    type: 'multiple', 
    question: "Welche Maßnahmen unterstützen eine verantwortungsvolle KI-Einführung im Mittelstand?",
    options: [
      "Schulungen & Zertifizierungen",
      "Regelmäßige Risiko-Audits",
      "Ignorieren gesetzlicher Vorgaben",
      "Festlegung klarer Verantwortlichkeiten"
    ],
    correctAnswers: [0, 1, 3]
  }
];

const TestPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [testState, setTestState] = useState<TestState>('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number[]>>({});
  const [score, setScore] = useState(0);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [certificateStatus, setCertificateStatus] = useState<{
    generating: boolean;
    sending: boolean;
    sent: boolean;
    error: string | null;
  }>({
    generating: false,
    sending: false,
    sent: false,
    error: null
  });

  // Early return for authentication check
  useEffect (()=> {
    if (!user) {
    return <Navigate to="/login\" replace />;
  }
  });
    
  

  const handleAnswerSelect = (answerIndex: number) => {
    const question = questions[currentQuestion];
    
    if (question.type === 'multiple') {
      const currentAnswers = selectedAnswers[currentQuestion] || [];
      const newAnswers = currentAnswers.includes(answerIndex)
        ? currentAnswers.filter(a => a !== answerIndex)
        : [...currentAnswers, answerIndex];
      
      setSelectedAnswers({
        ...selectedAnswers,
        [currentQuestion]: newAnswers
      });
    } else {
      setSelectedAnswers({
        ...selectedAnswers,
        [currentQuestion]: [answerIndex]
      });
    }
  };

  const calculateScore = () => {
    let correctAnswers = 0;
    
    questions.forEach((question, index) => {
      const userAnswers = selectedAnswers[index] || [];
      
      if (question.type === 'multiple') {
        const correct = question.correctAnswers;
        const isCorrect = userAnswers.length === correct.length &&
          userAnswers.every(a => correct.includes(a)) &&
          correct.every(a => userAnswers.includes(a));
        if (isCorrect) {
          correctAnswers++;
        }
      } else {
        if (userAnswers[0] === question.correctAnswer) {
          correctAnswers++;
        }
      }
    });
    
    return Math.round((correctAnswers / questions.length) * 100);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const finalScore = calculateScore();
    setScore(finalScore);
    setError(null);
    setTestState('results');
    
    if (finalScore >= 75) {
      try {
        setCertificateStatus({
          generating: true,
          sending: false,
          sent: false,
          error: null
        });
        
        // Generate certificate number
        const certNumber = `KI-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Store test result
        const { data: testResult, error: testError } = await supabase
          .from('test_results')
          .insert({
            user_id: user.id,
            score: finalScore,
            passed: finalScore >= 75,
            answers: selectedAnswers
          })
          .select()
          .single();

        if (testError) throw testError;

        // Create certificate
        const { data: certificate, error: certError } = await supabase
          .from('certificates')
          .insert({
            user_id: user.id,
            cert_number: certNumber,
            test_result_id: testResult.id
          })
          .select()  //
          .single(); //

        if (certError) throw certError;

        setCertificateStatus(prev => ({
          ...prev,
          generating: false,
          sending: true
        }));
        
        // Generate PDF on frontend and send to completion function
        try {
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          if (sessionError || !session) {
            console.error('Error getting session for Edge Function call:', sessionError);
            throw new Error('Authentication session not found.');
          }

          // Generate PDF using the same logic as CertificatePage
          const { generateCertificatePDF } = await import('../services/pdfService');
          const doc = await generateCertificatePDF({
            name: user.name,
            company: user.company,
            number: certificate.cert_number,
            date: new Date(certificate.issued_at).toLocaleDateString('de-DE')
          });
          
          const pdfDataUri = doc.output('datauristring');
          const pdfBase64 = pdfDataUri.split(',')[1];

          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/completion`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              user_id: user.id,
              cert_id: certificate.id, // Use the ID of the newly created certificate
              test_id: testResult.id,  // Use the ID of the newly created test result
              pdfContent: pdfBase64    // Send the generated PDF
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error calling completion Edge Function:', errorData);
            throw new Error(errorData.error || 'Failed to trigger certificate email.');
          }

          setCertificateStatus(prev => ({
            ...prev,
            sending: false,
            sent: true
          }));
          setEmailSent(true);

        } catch (edgeFunctionError) {
          console.error('Error triggering completion Edge Function:', edgeFunctionError);
          setCertificateStatus(prev => ({
            ...prev,
            generating: false,
            sending: false,
            error: 'Fehler beim Senden des Zertifikats per E-Mail. Bitte kontaktieren Sie den Support.'
          }));
          // Do not re-throw here, as the test result and certificate are already saved.
          // The user can still download the PDF manually.
        }
        
      } catch (error) {
        console.error('Error saving test result or certificate:', error);
        setCertificateStatus(prev => ({
          ...prev,
          generating: false,
          sending: false,
          error: 'Fehler beim Speichern des Testergebnisses oder Zertifikats. Bitte versuchen Sie es später erneut.'
        }));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleCertificateGeneration = async () => {
    try {
      // Get the latest certificate for this user
      const { data: certificate, error: certError } = await supabase
        .from('certificates')
        .select('id, cert_number, issued_at, test_result_id')
        .eq('user_id', user.id)
        .order('issued_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching certificate:', error);
        setError('Fehler beim Laden des Zertifikats');
        return;
      }

      const certData = {
        id: certificate.id,
        name: user.name,
        company: user.company,
        number: certificate.cert_number,
        date: new Date(certificate.issued_at).toLocaleDateString('de-DE'),
        testResultId: certificate.test_result_id
      };

      navigate('/zertifikat', { state: { certData } });
    } catch (error) {
      console.error('Error generating certificate:', error);
      setError('Fehler beim Generieren des Zertifikats');
    }
  };

  const currentQuestionData = questions[currentQuestion];
  const selectedAnswersForQuestion = selectedAnswers[currentQuestion] || [];

  if (testState === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
              KI-Kompetenznachweis nach EU AI Act
            </h1>
            
            <div className="prose max-w-none text-gray-600 mb-8">
              <p className="text-lg mb-4">
                Willkommen zum Abschlusstest für Ihren KI-Kompetenznachweis. 
                Dieser Test überprüft Ihr Verständnis der wichtigsten Aspekte des EU AI Acts 
                und des sicheren Umgangs mit KI-Systemen.
              </p>
              
              <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
                Wichtige Informationen
              </h2>
              
              <ul className="list-disc pl-5 space-y-2 mb-6">
                <li>Der Test besteht aus {questions.length} Fragen</li>
                <li>Es gibt Einzelauswahl- und Mehrfachauswahl-Fragen</li>
                <li>Zum Bestehen benötigen Sie mindestens 75%</li>
                <li>Sie können jede Frage in Ihrem eigenen Tempo beantworten</li>
                <li>Nach Abschluss erhalten Sie Ihr persönliches Zertifikat</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-2" />
                  <p className="text-blue-800">
                    Stellen Sie sicher, dass Sie alle Kursmodule abgeschlossen haben, 
                    bevor Sie mit dem Test beginnen.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setTestState('test')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              Test jetzt starten
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (testState === 'results') {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Testergebnis
            </h2>
            
            <div className="mb-8">
              <div className="flex items-center justify-center mb-6">
                <div className={`w-32 h-32 rounded-full flex items-center justify-center border-4 ${
                  score >= 75 ? 'border-green-500 text-green-600' : 'border-red-500 text-red-600'
                }`}>
                  <span className="text-4xl font-bold">{score}%</span>
                </div>
              </div>
              
              <p className="text-lg text-center mb-6">
                Sie haben {Math.round((score / 100) * questions.length)} von {questions.length} Fragen richtig beantwortet.
              </p>

              <div className="mb-8 border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-700">Detaillierte Auswertung</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {questions.map((question, index) => {
                    const userAnswers = selectedAnswers[index] || [];
                    let isCorrect = false;
                    
                    if (question.type === 'multiple') {
                      const correct = question.correctAnswers;
                      isCorrect = userAnswers.length === correct.length &&
                        userAnswers.every(a => correct.includes(a)) &&
                        correct.every(a => userAnswers.includes(a));
                    } else {
                      isCorrect = userAnswers[0] === question.correctAnswer;
                    }

                    return (
                      <div key={index} className="p-4">
                        <div className="flex items-start">
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                            isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {isCorrect ? '✓' : '✗'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 mb-2">
                              {question.question}
                            </p>
                            <div className="text-sm text-gray-600">
                              {question.type === 'multiple' ? (
                                <>
                                  <p className="mb-1">Richtige Antworten:</p>
                                  <ul className="list-disc pl-5">
                                    {question.correctAnswers.map(i => (
                                      <li key={i}>{question.options[i]}</li>
                                    ))}
                                  </ul>
                                </>
                              ) : (
                                <p>Richtige Antwort: {question.options[question.correctAnswer]}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {score >= 75 ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-8">
                  <p className="text-green-800 text-lg text-center">
                    Herzlichen Glückwunsch! Sie haben den Test bestanden.
                  </p>
                  
                  {/* Certificate Status */}
                  {(certificateStatus.generating || certificateStatus.sending || certificateStatus.sent || certificateStatus.error) && (
                    <div className="mt-6 p-4 rounded-lg border">
                      {certificateStatus.generating && (
                        <div className="flex items-center justify-center text-blue-700 bg-blue-50 border-blue-200">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600 mr-3"></div>
                          <span>Zertifikat wird generiert...</span>
                        </div>
                      )}
                      
                      {certificateStatus.sending && (
                        <div className="flex items-center justify-center text-blue-700 bg-blue-50 border-blue-200">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600 mr-3"></div>
                          <span>Zertifikat wird per E-Mail versendet...</span>
                        </div>
                      )}
                      
                      {certificateStatus.sent && (
                        <div className="flex items-center justify-center text-green-700 bg-green-50 border-green-200">
                          <svg className="w-5 h-5 mr-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Zertifikat wurde erfolgreich an {user.email} gesendet!</span>
                        </div>
                      )}
                      
                      {certificateStatus.error && (
                        <div className="flex items-start text-red-700 bg-red-50 border-red-200">
                          <svg className="w-5 h-5 mr-3 mt-0.5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="font-medium">Fehler beim Zertifikat-Versand</p>
                            <p className="text-sm mt-1">{certificateStatus.error}</p>
                            <p className="text-sm mt-2">Sie können das Zertifikat trotzdem manuell herunterladen.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-8 space-y-6">
                    <button
                      onClick={handleCertificateGeneration}
                      disabled={certificateStatus.generating || certificateStatus.sending}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-4 px-8 rounded-lg transition-all transform hover:scale-105 text-lg"
                    >
                      {certificateStatus.generating || certificateStatus.sending 
                        ? 'Zertifikat wird verarbeitet...' 
                        : 'Zertifikat anzeigen und herunterladen'
                      }
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-8">
                  <p className="text-red-800 text-lg text-center">
                    Leider haben Sie den Test nicht bestanden. 
                    Sie benötigen mindestens 75% für ein Zertifikat.
                  </p>
                  
                  <p className="text-red-700 mt-6 text-center">
                    Wir empfehlen Ihnen, die Kursmodule noch einmal durchzugehen 
                    und sich die wichtigsten Punkte zu notieren.
                  </p>
                  
                  <button
                    onClick={() => {
                      setTestState('test');
                      setCurrentQuestion(0);
                      setSelectedAnswers({});
                    }}
                    className="w-full mt-8 bg-red-600 hover:bg-red-700 text-white font-medium py-4 px-8 rounded-lg transition-all transform hover:scale-105 text-lg"
                  >
                    Test wiederholen
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-red-700">{error}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test interface
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Frage {currentQuestion + 1} von {questions.length}</span>
              <span>{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-in-out"
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800 mb-8">
              {currentQuestionData.question}
            </h2>

            <div className="space-y-3">
              {currentQuestionData.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  className={`w-full text-left p-5 rounded-lg border-2 transition-all transform hover:scale-[1.02] ${
                    selectedAnswersForQuestion.includes(index)
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-6 h-6 flex-shrink-0 border ${
                      currentQuestionData.type === 'multiple' ? 'rounded-md' : 'rounded-full'
                    } mr-4 ${
                      selectedAnswersForQuestion.includes(index)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedAnswersForQuestion.includes(index) && (
                        <svg className="w-6 h-6 text-white\" fill="none\" viewBox="0 0 24 24\" stroke="currentColor">
                          <path strokeLinecap="round\" strokeLinejoin="round\" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-gray-700 text-lg">{option}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-between mt-10">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className={`px-8 py-3 rounded-lg text-lg transition-all ${
                  currentQuestion === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                }`}
              >
                Zurück
              </button>
              
              {currentQuestion < questions.length - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={!selectedAnswers[currentQuestion]}
                  className={`px-8 py-3 rounded-lg text-lg transition-all transform hover:scale-105 ${
                    !selectedAnswers[currentQuestion]
                      ? 'bg-blue-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  Weiter
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!selectedAnswers[currentQuestion] || isSubmitting}
                  className={`px-8 py-3 rounded-lg text-lg transition-all transform hover:scale-105 ${
                    !selectedAnswers[currentQuestion] || isSubmitting
                      ? 'bg-green-300 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isSubmitting ? 'Wird gespeichert...' : 'Test abschließen'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPage;